import { useCallback, useEffect, useRef, useState } from "react";
import { apiApont, type RoteiroLinha, type EtapaDisponivel } from "../lib/api";
import { formatDecimal } from "../lib/format";
import { MsgModal } from "./MsgModal";
import { mascaraTempoHM } from "../lib/tempoHM";

interface Props {
  numpcf: number;
  senha: string; // senha do operador já validada (VER-SENHA habilita o botão adicionar)
  onImprimir: () => void; // botao-imprimir -> r1cadpcf
  onClose: () => void;
}

// PCF_ETAPAS — cadastro/edição do roteiro de etapas de uma PCF.
// Fiel ao form pcf_etapas do acompanhamento_pcf: combo de etapa, dimensões, características,
// tempo; incluir; imprimir. Subir/descer (troca com a linha vizinha) = arrastar a linha.
export function PcfRoteiroModal({ numpcf, senha, onImprimir, onClose }: Props) {
  const [linhas, setLinhas] = useState<RoteiroLinha[]>([]);
  const [etapas, setEtapas] = useState<EtapaDisponivel[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [dragIdx, setDragIdx] = useState<number | null>(null); // linha sendo arrastada
  const [overIdx, setOverIdx] = useState<number | null>(null); // alvo do arraste
  const [busy, setBusy] = useState(false);

  const [etapaCodigo, setEtapaCodigo] = useState<number | "">("");
  const [dimensionais, setDimensionais] = useState("");
  const [caracteristicas, setCaracteristicas] = useState("");
  const [tempo, setTempo] = useState("");

  const refEtapa = useRef<HTMLSelectElement>(null);
  const refDimens = useRef<HTMLInputElement>(null);
  const refCaract = useRef<HTMLInputElement>(null);
  const refTempo = useRef<HTMLInputElement>(null);

  const carregar = useCallback(() => {
    apiApont.roteiro(numpcf).then(setLinhas).catch((e) => setErro((e as Error).message));
  }, [numpcf]);

  useEffect(() => {
    carregar();
    apiApont.etapasDisponiveis().then(setEtapas).catch(() => setEtapas([]));
  }, [carregar]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const limparCampos = () => {
    setEtapaCodigo("");
    setDimensionais("");
    setCaracteristicas("");
    setTempo("");
    setTimeout(() => refEtapa.current?.focus(), 30);
  };

  // ROTINA-INCLUSAO (L304-336): posição = max + 1.
  const incluir = useCallback(async () => {
    setErro(null);
    const tempoEtapa = Number((tempo || "").replace(",", "."));
    const r = await apiApont.roteiroIncluir({
      numpcf,
      etapaCodigo: Number(etapaCodigo || 0),
      dimensionais,
      caracteristicas,
      tempoEtapa,
      senha,
    });
    if (!r.ok) {
      setErro(r.erro ?? "Falha ao incluir.");
      return;
    }
    carregar();
    limparCampos();
  }, [numpcf, etapaCodigo, dimensionais, caracteristicas, tempo, senha, carregar]);

  // Arrastar = botao-subir/botao-descer (L500-595): troca com a vizinha, repetida até a linha alvo.
  const arrastar = useCallback(
    async (from: number, to: number) => {
      if (from === to || busy) return;
      setBusy(true);
      setErro(null);
      const direcao = to < from ? "subir" : "descer";
      const passo = to < from ? -1 : 1;
      const posicoes = linhas.map((l) => l.posicao);
      let pos = posicoes[from];
      for (let i = from; i !== to && pos !== undefined; i += passo) {
        const r = await apiApont.roteiroMover(numpcf, pos, direcao, senha);
        if (!r.ok) {
          setErro(r.erro ?? "Falha ao mover.");
          break;
        }
        pos = posicoes[i + passo];
      }
      carregar();
      setBusy(false);
    },
    [busy, linhas, numpcf, senha, carregar]
  );

  // Enter encadeado (regra Enter=Tab): etapa → dimens → caract → tempo → incluir.
  const enterPara = (proximo: React.RefObject<HTMLElement>, ehUltimo = false) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (ehUltimo) incluir();
      else proximo.current?.focus();
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel modal-roteiro" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <div className="modal-eyebrow">Plano de Controle de Fabricação - Etapas da Produção</div>
            <h2>PCF nº {numpcf}</h2>
          </div>
          <button className="btn-secundario" onClick={onClose} title="Voltar (Esc)">Voltar</button>
        </header>

        <section className="roteiro-form">
          <label className="rf-etapa">
            <span>Etapa</span>
            <select
              ref={refEtapa}
              value={etapaCodigo}
              onChange={(e) => setEtapaCodigo(e.target.value ? Number(e.target.value) : "")}
              onKeyDown={enterPara(refDimens)}
            >
              <option value="">Selecione…</option>
              {etapas.map((et) => (
                <option key={et.codigo} value={et.codigo}>{et.descri}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Dimensões</span>
            <input ref={refDimens} value={dimensionais} onChange={(e) => setDimensionais(e.target.value)} onKeyDown={enterPara(refCaract)} maxLength={50} />
          </label>
          <label>
            <span>Características</span>
            <input ref={refCaract} value={caracteristicas} onChange={(e) => setCaracteristicas(e.target.value)} onKeyDown={enterPara(refTempo)} maxLength={50} />
          </label>
          <label className="rf-tempo">
            <span>Duração (hhhh,mm)</span>
            <input
              ref={refTempo}
              value={tempo}
              onChange={(e) => setTempo(mascaraTempoHM(e.target.value, tempo))}
              onKeyDown={enterPara(refTempo, true)}
              placeholder="0,30"
              inputMode="decimal"
            />
          </label>
          <button className="btn-acao azul rf-incluir" onClick={incluir}>Incluir Etapa</button>
        </section>

        <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />

        <div className="roteiro-acoes">
          <span className="roteiro-dica">Arraste as linhas para reordenar as etapas.</span>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onImprimir}>Imprimir</button>
        </div>
        <section className="roteiro-tabela">
          <table className="apont-hist roteiro-grid">
            <thead>
              <tr>
                <th className="col-pos">Posição</th>
                <th>Etapa</th>
                <th>Descrição</th>
                <th>Características</th>
                <th className="r">Duração</th>
              </tr>
            </thead>
            <tbody>
              {linhas.length === 0 ? (
                <tr><td colSpan={5} className="empty">Nenhuma etapa cadastrada.</td></tr>
              ) : (
                linhas.map((l, i) => (
                  <tr
                    key={l.posicao}
                    draggable={!busy}
                    onDragStart={() => setDragIdx(i)}
                    onDragEnter={() => setOverIdx(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragIdx !== null) void arrastar(dragIdx, i);
                      setDragIdx(null);
                      setOverIdx(null);
                    }}
                    onDragEnd={() => {
                      setDragIdx(null);
                      setOverIdx(null);
                    }}
                    className={
                      (dragIdx === i ? "dragging" : "") +
                      (overIdx === i && dragIdx !== null && dragIdx !== i ? " drop-over" : "")
                    }
                  >
                    <td className="col-pos"><span className="drag-handle">⠿</span> {l.posicao}</td>
                    <td>{l.descricao}</td>
                    <td>{l.dimensionais}</td>
                    <td>{l.caracteristicas}</td>
                    <td className="r">{formatDecimal(l.tempoEtapa, 2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
